import { useEditorState } from "@tiptap/react";
import type { Editor } from "@tiptap/core";
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  List,
  ListOrdered,
} from "lucide-react";
import { ToolbarButton } from "../shared/richTextEditorToolbarButton";
import { IconWithAccessibilityBadge } from "../shared/iconWithAccessibilityBadge";
import { runFormat } from "../shared/richTextEditorFormatUtils";
import {
  RTE_TOOLTIP_STRONG,
  RTE_TOOLTIP_EMPHASIZED,
} from "@/constants/uiCopy/richTextEditor";
import {
  RteFeature,
  featureBlacklistHas,
} from "../../richTextEditorFeatureBlacklist";
import { selectRichTextToolbarActiveState } from "../shared/selectRichTextToolbarActiveState";

export const BubbleMenuToolbar = ({
  editor,
  featureBlacklist,
}: {
  editor: Editor;
  featureBlacklist?: readonly RteFeature[];
}) => {
  const state = useEditorState({
    editor,
    selector: selectRichTextToolbarActiveState,
  });
  if (state == null) return null;

  const showBold = !featureBlacklistHas(featureBlacklist, RteFeature.bold);
  const showItalic = !featureBlacklistHas(featureBlacklist, RteFeature.italic);
  const showStrong = !featureBlacklistHas(featureBlacklist, RteFeature.strong);
  const showEmphasized = !featureBlacklistHas(
    featureBlacklist,
    RteFeature.emphasized,
  );
  const showBulletList = !featureBlacklistHas(
    featureBlacklist,
    RteFeature.bulletList,
  );
  const showOrderedList = !featureBlacklistHas(
    featureBlacklist,
    RteFeature.orderedList,
  );

  return (
    <>
      {showBold ? (
        <ToolbarButton
          onClick={() =>
            runFormat(true, editor, {
              toggle: () => editor.chain().focus().toggleBold().run(),
              wrap: { open: "**", close: "**" },
            })
          }
          aria-pressed={state.isBold}
        >
          <BoldIcon className="size-4" />
        </ToolbarButton>
      ) : null}
      {showItalic ? (
        <ToolbarButton
          onClick={() =>
            runFormat(true, editor, {
              toggle: () => editor.chain().focus().toggleItalic().run(),
              wrap: { open: "*", close: "*" },
            })
          }
          aria-pressed={state.isItalic}
        >
          <ItalicIcon className="size-4" />
        </ToolbarButton>
      ) : null}
      {showStrong ? (
        <ToolbarButton
          onClick={() =>
            runFormat(true, editor, {
              toggle: () => editor.chain().focus().toggleBold().run(),
              wrap: { open: "__", close: "__" },
            })
          }
          aria-label={RTE_TOOLTIP_STRONG}
          aria-pressed={state.isStrong}
        >
          <IconWithAccessibilityBadge className="size-4">
            <BoldIcon className="size-4" />
          </IconWithAccessibilityBadge>
        </ToolbarButton>
      ) : null}
      {showEmphasized ? (
        <ToolbarButton
          onClick={() =>
            runFormat(true, editor, {
              toggle: () => editor.chain().focus().toggleItalic().run(),
              wrap: { open: "_", close: "_" },
            })
          }
          aria-label={RTE_TOOLTIP_EMPHASIZED}
          aria-pressed={state.isEmphasized}
        >
          <IconWithAccessibilityBadge className="size-4">
            <ItalicIcon className="size-4" />
          </IconWithAccessibilityBadge>
        </ToolbarButton>
      ) : null}
      {showBulletList ? (
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          aria-pressed={state.isBulletList}
        >
          <List className="size-4" />
        </ToolbarButton>
      ) : null}
      {showOrderedList ? (
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          aria-pressed={state.isOrderedList}
        >
          <ListOrdered className="size-4" />
        </ToolbarButton>
      ) : null}
    </>
  );
};
